import { useTranslation } from 'react-i18next';
import { Column, ColumnDef } from '@tanstack/react-table';
import { Button } from '@/components/ui/button';
import ArrowDownIconSolid from '@/components/icons/ArrowDownIconSolid';
import EDateText from '@/components/everridge/EDateText';
import {
    TransactionListItemResponseDto,
    TransactionType,
} from '@/features/transactions/types/transactions-dto';
import TransactionsAccountDetails from '@/features/transactions/components/TransactionsAccountDetails';
import { useTransactionsFilter } from '@/features/transactions/hooks/useTransactionsFilter';
import { TransactionsUtils } from '../utils/transactionsUtils';
import CurrencyValue from '@/features/finances/components/CurrencyValue';
import { CurrencyAssetType } from '@/utils/CurrencyHelper';

const TransactionsPageColumns = (): ColumnDef<TransactionListItemResponseDto>[] => {
    const { t } = useTranslation();

    const { setFilter } = useTransactionsFilter();

    const handleSort = (
        columnKey: Capitalize<keyof TransactionListItemResponseDto>,
        column: Column<TransactionListItemResponseDto>,
    ) => {
        const newDirection = column.getIsSorted() === 'asc';
        column.toggleSorting(newDirection);

        setFilter({
            orderBy: columnKey,
            orderDirection: newDirection ? 'asc' : 'desc',
        });
    };

    const columnClasses = 'text-sm text-popover-800';

    return [
        {
            accessorKey: 'date',
            header: ({ column }) => {
                return (
                    <Button
                        variant="link"
                        className={
                            'h-fit p-0 flex items-center gap-2.5 text-sm text-popover-1000 font-medium no-underline hover:no-underline whitespace-pre-wrap'
                        }
                        onClick={() => handleSort('Date', column)}
                    >
                        {t('main.transactions.table.header.timestamp')}
                        <div
                            className={`transition-transform duration-50 ease-in-out ${
                                column.getIsSorted() === 'asc' ? '-rotate-180' : 'rotate-0'
                            }`}
                        >
                            {<ArrowDownIconSolid />}
                        </div>
                    </Button>
                );
            },
            cell: ({ row }) => (
                <div className={columnClasses}>
                    <EDateText date={row.getValue('date')} />
                </div>
            ),
        },
        {
            accessorKey: 'serviceName',
            header: ({ column }) => {
                return (
                    <Button
                        variant="link"
                        className={
                            'h-fit p-0 flex items-center gap-2.5 text-sm text-popover-1000 font-medium no-underline hover:no-underline whitespace-pre-wrap'
                        }
                        onClick={() => handleSort('ServiceName', column)}
                    >
                        {t('main.transactions.table.header.description')}
                        <div
                            className={`transition-transform duration-50 ease-in-out ${
                                column.getIsSorted() === 'asc' ? '-rotate-180' : 'rotate-0'
                            }`}
                        >
                            {<ArrowDownIconSolid />}
                        </div>
                    </Button>
                );
            },
            cell: ({ row }) => (
                <div className={`truncate ${columnClasses}`}>
                    <span>{row.getValue('serviceName')}</span>
                </div>
            ),
        },
        {
            accessorKey: 'apartmentName',
            header: ({ column }) => {
                return (
                    <Button
                        variant="link"
                        className={
                            'h-fit p-0 flex items-center gap-2.5 text-sm text-popover-1000 font-medium no-underline hover:no-underline whitespace-pre-wrap'
                        }
                        onClick={() => handleSort('ApartmentName', column)}
                    >
                        {t('main.transactions.table.header.apartment')}
                        <div
                            className={`transition-transform duration-50 ease-in-out ${
                                column.getIsSorted() === 'asc' ? '-rotate-180' : 'rotate-0'
                            }`}
                        >
                            {<ArrowDownIconSolid />}
                        </div>
                    </Button>
                );
            },
            cell: ({ row }) => (
                <div className={`truncate ${columnClasses}`}>
                    <span>{row.getValue('apartmentName')}</span>
                </div>
            ),
        },
        {
            accessorKey: 'sign',
            header: ({ column }) => {
                return (
                    <Button
                        variant="link"
                        className={
                            'h-fit p-0 flex items-center gap-2.5 text-sm text-popover-1000 font-medium no-underline hover:no-underline whitespace-pre-wrap'
                        }
                        onClick={() => handleSort('Sign', column)}
                    >
                        {t('main.transactions.table.header.transactionType')}
                        <div
                            className={`transition-transform duration-50 ease-in-out ${
                                column.getIsSorted() === 'asc' ? '-rotate-180' : 'rotate-0'
                            }`}
                        >
                            {<ArrowDownIconSolid />}
                        </div>
                    </Button>
                );
            },
            cell: ({ row }) => {
                const {
                    label: feeLabel,
                    icon: FeeIcon,
                    iconStyles: feeIconStyles,
                } = TransactionsUtils.getFeeConfig(row.getValue('sign'));

                return (
                    <div className={`flex gap-1.5 truncate ${columnClasses}`}>
                        <FeeIcon className={feeIconStyles} />
                        <span>{t(`${feeLabel}`)}</span>
                    </div>
                );
            },
        },
        {
            accessorKey: 'deductionFromRentRevenue',
            header: ({ column }) => {
                return (
                    <Button
                        variant="link"
                        className={
                            'h-fit p-0 flex items-center gap-2.5 text-sm text-popover-1000 font-medium no-underline hover:no-underline whitespace-pre-wrap'
                        }
                        onClick={() => handleSort('DeductionFromRentRevenue', column)}
                    >
                        {t('main.transactions.table.header.deductionFromRent')}
                        <div
                            className={`transition-transform duration-50 ease-in-out ${
                                column.getIsSorted() === 'asc' ? '-rotate-180' : 'rotate-0'
                            }`}
                        >
                            {<ArrowDownIconSolid />}
                        </div>
                    </Button>
                );
            },
            cell: ({ row }) => {
                const { icon: DedactionIcon, iconStyles: dedactionIconStyles } =
                    TransactionsUtils.getDeductionConfig(row.getValue('deductionFromRentRevenue'));

                return (
                    <div className={`truncate ${columnClasses}`}>
                        <DedactionIcon className={dedactionIconStyles} />
                    </div>
                );
            },
        },
        {
            accessorKey: 'type',
            header: ({ column }) => {
                return (
                    <Button
                        variant="link"
                        className={
                            'h-fit p-0 flex items-center gap-2.5 text-sm text-popover-1000 font-medium no-underline hover:no-underline whitespace-pre-wrap'
                        }
                        onClick={() => handleSort('Type', column)}
                    >
                        {t('main.transactions.table.header.account')}
                        <div
                            className={`transition-transform duration-50 ease-in-out ${
                                column.getIsSorted() === 'asc' ? '-rotate-180' : 'rotate-0'
                            }`}
                        >
                            {<ArrowDownIconSolid />}
                        </div>
                    </Button>
                );
            },
            cell: ({ row }) => (
                <div className={`truncate ${columnClasses}`}>
                    <TransactionsAccountDetails account={row.getValue('type')} />
                </div>
            ),
        },
        {
            accessorKey: 'amount',
            header: ({ column }) => {
                return (
                    <Button
                        variant="link"
                        className={
                            'h-fit p-0 flex items-center gap-2.5 text-sm text-popover-1000 font-medium no-underline hover:no-underline whitespace-pre-wrap'
                        }
                        onClick={() => handleSort('Amount', column)}
                    >
                        {t('main.transactions.table.header.amount')}
                        <div
                            className={`transition-transform duration-50 ease-in-out ${
                                column.getIsSorted() === 'asc' ? '-rotate-180' : 'rotate-0'
                            }`}
                        >
                            {<ArrowDownIconSolid />}
                        </div>
                    </Button>
                );
            },
            cell: ({ row }) => (
                <div className={`truncate ${columnClasses}`}>
                    <CurrencyValue
                        value={row.getValue('amount')}
                        asset={
                            TransactionType[row.original.type].toLowerCase() as CurrencyAssetType
                        }
                        addStyles={true}
                    />
                </div>
            ),
        },
    ];
};

export default TransactionsPageColumns;
